import { httpClient } from './client';

import type { BulkLoadResponse } from '../types/bulkload-response';
import type { AsyncTaskErrorEnvelope } from '../types/common';
import type { CustomerLtlDefaults } from '../types/customer';
import type { SellerFreightProvider } from '../types/freight-providers';
import type {
  BookFreightFulfillmentPlanRequest,
  BulkDeleteFreightPresetRequest,
  BulkPrintFreightDocumentsRequest,
  CancelFreightFulfillmentPlanRequest,
  CustomExtractExportFreightRequest,
  CustomExtractExportFreightShipmentsRequest,
  FreightCalculateDensityRequest,
  FreightCommodityPresetGridRequest,
  FreightCustomAttributeGridRequest,
  FreightFulfillmentPlanExportRequest,
  FreightFulfillmentPlanGridRequest,
  FreightFulfillmentPlanSaveQuoteRequest,
  FreightGridStatusCountsRequest,
  FreightHandlingUnitPresetGridRequest,
  FreightQuoteRequest,
  FreightShipmentExportRequest,
  FreightShipmentGridRequest,
  FreightTerminalDataRequest,
  FreightTrackShipmentRequest,
  SaveFreightConfigurationRequest,
  VoidFreightRequest,
} from '../types/freight-requests';
import type {
  BulkDeleteFreightPresetResponse,
  FreightCalculateDensityResponse,
  FreightCommodityPresetGridResponse,
  FreightCustomAttributeAvailable,
  FreightCustomAttributeDefinition,
  FreightCustomAttributeGridResponse,
  FreightFulfillmentPlanCommodityPreset,
  FreightFulfillmentPlanGridResponse,
  FreightGridStatusCountsResponse,
  FreightFulfillmentPlanHandlingUnitPreset,
  FreightHandlingUnitPresetGridResponse,
  FreightShipment,
  FreightShipmentGridResponse,
  FreightTerminalDataResponse,
  FreightTrackShipmentResponse,
  VoidFreightResponse,
} from '../types/freight-responses';
import type { LtlDefaults } from '../types/ship-from';
import type { AxiosRequestConfig } from 'axios';

// setup-axios.js's transformRequest converts any field named `shipDate`
// to a full ISO datetime string via moment().toISOString(). Freight endpoints need
// shipDate as a plain date (YYYY-MM-DD), so these calls opt out of that transform.
const DATE_ONLY_CONFIG: AxiosRequestConfig = {
  transformRequest: [(data: unknown) => JSON.stringify(data)],
  headers: { 'Content-Type': 'application/json; charset=utf-8' },
};

export const getQuotes = (payload: FreightQuoteRequest): Promise<AsyncTaskErrorEnvelope> => {
  return httpClient.post('/freight/quote', payload).then((r) => r.data);
};

export const calculateDensity = (payload: FreightCalculateDensityRequest): Promise<FreightCalculateDensityResponse> => {
  return httpClient.post('/freight/calculate-density', payload).then((r) => r.data);
};

export const quoteFulfillmentPlan = (fulfillmentPlanId: string): Promise<AsyncTaskErrorEnvelope> => {
  return httpClient.post(`/freight/${fulfillmentPlanId}/quote`).then((r) => r.data);
};

export const bookFulfillmentPlan = (payload: BookFreightFulfillmentPlanRequest): Promise<AsyncTaskErrorEnvelope> => {
  return httpClient.post('/freight/fulfillment-plan/book', payload).then((r) => r.data);
};

export const trackShipment = (payload: FreightTrackShipmentRequest): Promise<FreightTrackShipmentResponse> => {
  return httpClient.post('/freight/track', payload).then((r) => r.data);
};

export const bulkPrintFreightDocuments = (payload: BulkPrintFreightDocumentsRequest): Promise<string[]> => {
  return httpClient.post('/freight/document/async', payload).then((r) => r.data.jobGuids);
};

export const combinedFreightDocumentsAsync = (freightShipmentId: string): Promise<string[]> => {
  return httpClient.post('/freight/document/async/combined', { freightShipmentId }).then((r) => r.data.jobGuids);
};

export const cancelFulfillmentPlans = (payload: CancelFreightFulfillmentPlanRequest): Promise<void> => {
  return httpClient.post('/freight/fulfillment-plan/cancel', payload).then(() => undefined);
};

export const voidFreight = (payload: VoidFreightRequest): Promise<VoidFreightResponse> => {
  return httpClient.post('/freight/void', payload).then((r) => r.data);
};

export const getBookedShipment = (freightShipmentId: string): Promise<FreightShipment> => {
  return httpClient.get(`/freight/shipment/${freightShipmentId}`).then((r) => r.data);
};

export const getFreightFulfillmentPlans = (payload: FreightFulfillmentPlanGridRequest): Promise<FreightFulfillmentPlanGridResponse> => {
  return httpClient.post('/freightfulfillmentplangrid', payload).then((r) => r.data);
};

/** Shipment-keyed sibling of {@link getFreightFulfillmentPlans}, backing the LTL Voided view. */
export const getFreightShipmentGrid = (payload: FreightShipmentGridRequest): Promise<FreightShipmentGridResponse> => {
  return httpClient.post('/FreightShipmentGrid', payload).then((r) => r.data);
};

/** Per-status aggregate counts for the LTL quicksearch nav. */
export const getFreightGridStatusCounts = (payload: FreightGridStatusCountsRequest): Promise<FreightGridStatusCountsResponse> => {
  return httpClient.post('/FreightGridStatusCounts', payload).then((r) => r.data);
};

export const saveQuoteToFulfillmentPlan = (payload: FreightFulfillmentPlanSaveQuoteRequest): Promise<BulkLoadResponse> => {
  return httpClient.post('/freight/fulfillment-plan/save-quote', payload, DATE_ONLY_CONFIG).then((r) => r.data);
};

export const saveConfiguration = (fulfillmentPlanId: string, request: SaveFreightConfigurationRequest): Promise<BulkLoadResponse> => {
  return httpClient.put(`/freight/${fulfillmentPlanId}/save-configuration`, request, DATE_ONLY_CONFIG).then((r) => r.data);
};

export const exportFreightShipments = (payload: FreightFulfillmentPlanExportRequest): Promise<AsyncTaskErrorEnvelope> => {
  return httpClient.post('/freight/export', payload).then((r) => r.data);
};

/** Shipment-keyed sibling of {@link exportFreightShipments}: ids are freight shipment ids, not plan ids. */
export const exportFreightShipmentsByShipment = (payload: FreightShipmentExportRequest): Promise<AsyncTaskErrorEnvelope> => {
  return httpClient.post('/freight/export/shipments', payload).then((r) => r.data);
};

export const exportFreightShipmentsCustomFormat = (payload: CustomExtractExportFreightRequest): Promise<AsyncTaskErrorEnvelope> => {
  return httpClient.post('/freight/export/custom-format', payload).then((r) => r.data);
};

/** Shipment-keyed sibling of {@link exportFreightShipmentsCustomFormat}: ids are freight shipment ids, not plan ids. */
export const exportFreightShipmentsCustomFormatByShipment = (
  payload: CustomExtractExportFreightShipmentsRequest,
): Promise<AsyncTaskErrorEnvelope> => {
  return httpClient.post('/freight/export/custom-format/shipments', payload).then((r) => r.data);
};

export const getSellerProviders = (): Promise<SellerFreightProvider[]> => {
  return httpClient.get<SellerFreightProvider[]>('/freight/seller-provider').then((r) => r.data);
};

export const updateSellerProviderNickname = (sellerFreightProviderId: number, nickname: string): Promise<void> => {
  return httpClient.patch(`/freight/seller-provider/${sellerFreightProviderId}/nickname`, { nickname }).then(() => undefined);
};

export const getFreightFulfillmentPlanCommodityPresets = (
  search: string,
  signal?: AbortSignal,
): Promise<FreightFulfillmentPlanCommodityPreset[]> => {
  return httpClient
    .get<FreightFulfillmentPlanCommodityPreset[]>('/freight/commodity-preset', { params: { search }, signal })
    .then((r) => r.data ?? []);
};

export const getFreightCommodityPresetGrid = (payload: FreightCommodityPresetGridRequest): Promise<FreightCommodityPresetGridResponse> =>
  httpClient.post<FreightCommodityPresetGridResponse>('/freight/commodity-preset/grid', payload).then((r) => r.data);

export const getFreightCommodityPreset = (commodityPresetId: number): Promise<FreightFulfillmentPlanCommodityPreset> =>
  httpClient.get<FreightFulfillmentPlanCommodityPreset>(`/freight/commodity-preset/${commodityPresetId}`).then((r) => r.data);

export const createFreightCommodityPreset = (
  payload: FreightFulfillmentPlanCommodityPreset,
): Promise<FreightFulfillmentPlanCommodityPreset> =>
  httpClient.post<FreightFulfillmentPlanCommodityPreset>('/freight/commodity-preset', payload).then((r) => r.data);

export const updateFreightCommodityPreset = (
  commodityPresetId: number,
  payload: FreightFulfillmentPlanCommodityPreset,
): Promise<FreightFulfillmentPlanCommodityPreset> =>
  httpClient.put<FreightFulfillmentPlanCommodityPreset>(`/freight/commodity-preset/${commodityPresetId}`, payload).then((r) => r.data);

export const deleteFreightCommodityPreset = (commodityPresetId: number): Promise<void> =>
  httpClient.delete(`/freight/commodity-preset/${commodityPresetId}`).then(() => undefined);

export const bulkDeleteFreightCommodityPresets = (payload: BulkDeleteFreightPresetRequest): Promise<BulkDeleteFreightPresetResponse> =>
  httpClient.post<BulkDeleteFreightPresetResponse>('/freight/commodity-preset/bulk-delete', payload).then((r) => r.data);

/** Saves LTL (freight) defaults for a ship-from location. */
export const saveShipFromLtlDefaults = (shipFromId: string, payload: LtlDefaults): Promise<void> =>
  httpClient.put(`/seller/shipfrom/${shipFromId}/ltl-defaults`, payload).then(() => undefined);

/** Fetches a customer's LTL Defaults (ship-to twin of the ship-from endpoints above). */
export const getCustomerLtlDefaults = (customerId: string): Promise<CustomerLtlDefaults | null> =>
  httpClient.get<CustomerLtlDefaults | null>(`/customer/${customerId}/ltl-defaults`).then((r) => r.data ?? null);

/** Saves LTL (freight) defaults for a customer. */
export const saveCustomerLtlDefaults = (customerId: string, payload: CustomerLtlDefaults): Promise<void> =>
  httpClient.put(`/customer/${customerId}/ltl-defaults`, payload).then(() => undefined);

export const getFreightFulfillmentPlanHandlingUnitPresets = (
  search: string,
  signal?: AbortSignal,
): Promise<FreightFulfillmentPlanHandlingUnitPreset[]> => {
  return httpClient
    .get<FreightFulfillmentPlanHandlingUnitPreset[]>('/freight/handling-unit-preset', { params: { search }, signal })
    .then((r) => r.data ?? []);
};

export const getAllFreightHandlingUnitPresets = (): Promise<FreightFulfillmentPlanHandlingUnitPreset[]> =>
  httpClient.get<FreightFulfillmentPlanHandlingUnitPreset[]>('/freight/handling-unit-preset/all').then((r) => r.data ?? []);

export const getFreightHandlingUnitPresetGrid = (
  payload: FreightHandlingUnitPresetGridRequest,
): Promise<FreightHandlingUnitPresetGridResponse> =>
  httpClient.post<FreightHandlingUnitPresetGridResponse>('/freight/handling-unit-preset/grid', payload).then((r) => r.data);

export const getFreightHandlingUnitPreset = (handlingUnitPresetId: number): Promise<FreightFulfillmentPlanHandlingUnitPreset> =>
  httpClient.get<FreightFulfillmentPlanHandlingUnitPreset>(`/freight/handling-unit-preset/${handlingUnitPresetId}`).then((r) => r.data);

export const createFreightHandlingUnitPreset = (
  payload: FreightFulfillmentPlanHandlingUnitPreset,
): Promise<FreightFulfillmentPlanHandlingUnitPreset> =>
  httpClient.post<FreightFulfillmentPlanHandlingUnitPreset>('/freight/handling-unit-preset', payload).then((r) => r.data);

export const updateFreightHandlingUnitPreset = (
  handlingUnitPresetId: number,
  payload: FreightFulfillmentPlanHandlingUnitPreset,
): Promise<FreightFulfillmentPlanHandlingUnitPreset> =>
  httpClient
    .put<FreightFulfillmentPlanHandlingUnitPreset>(`/freight/handling-unit-preset/${handlingUnitPresetId}`, payload)
    .then((r) => r.data);

export const deleteFreightHandlingUnitPreset = (handlingUnitPresetId: number): Promise<void> =>
  httpClient.delete(`/freight/handling-unit-preset/${handlingUnitPresetId}`).then(() => undefined);

export const bulkDeleteFreightHandlingUnitPresets = (payload: BulkDeleteFreightPresetRequest): Promise<BulkDeleteFreightPresetResponse> =>
  httpClient.post<BulkDeleteFreightPresetResponse>('/freight/handling-unit-preset/bulk-delete', payload).then((r) => r.data);

export const getAvailableFreightCustomAttributes = (): Promise<FreightCustomAttributeAvailable[]> =>
  httpClient.get<FreightCustomAttributeAvailable[]>('/freight/custom-attribute').then((r) => r.data ?? []);

export const getFreightCustomAttributeGrid = (payload: FreightCustomAttributeGridRequest): Promise<FreightCustomAttributeGridResponse> =>
  httpClient.post<FreightCustomAttributeGridResponse>('/freight/custom-attribute/grid', payload).then((r) => r.data);

export const getFreightCustomAttribute = (definitionId: number): Promise<FreightCustomAttributeDefinition> =>
  httpClient.get<FreightCustomAttributeDefinition>(`/freight/custom-attribute/${definitionId}`).then((r) => r.data);

export const createFreightCustomAttribute = (payload: FreightCustomAttributeDefinition): Promise<FreightCustomAttributeDefinition> =>
  httpClient.post<FreightCustomAttributeDefinition>('/freight/custom-attribute', payload).then((r) => r.data);

export const updateFreightCustomAttribute = (
  definitionId: number,
  payload: FreightCustomAttributeDefinition,
): Promise<FreightCustomAttributeDefinition> =>
  httpClient.put<FreightCustomAttributeDefinition>(`/freight/custom-attribute/${definitionId}`, payload).then((r) => r.data);

export const setFreightCustomAttributeStatus = (definitionId: number, isActive: boolean): Promise<FreightCustomAttributeDefinition> =>
  httpClient.put<FreightCustomAttributeDefinition>(`/freight/custom-attribute/${definitionId}/status`, { isActive }).then((r) => r.data);

export const emailFreightDocument = (freightShipmentId: string, emailAddress: string, documentTypes?: string[]): Promise<void> =>
  httpClient.post(`/freight/shipment/${freightShipmentId}/email-document`, { emailAddress, documentTypes }).then(() => undefined);

export const emailFreightDocumentByUrl = (freightShipmentId: string, emailAddress: string, documentUrl: string): Promise<void> =>
  httpClient.post(`/freight/shipment/${freightShipmentId}/email-document`, { emailAddress, documentUrl }).then(() => undefined);

export const getFreightTerminalData = (request: FreightTerminalDataRequest): Promise<FreightTerminalDataResponse> => {
  return httpClient
    .get<FreightTerminalDataResponse>('/freight/terminal-data', {
      params: {
        originCode: request.originCode,
        destinationCode: request.destinationCode,
        scac: request.scac,
        sellerFreightProviderId: request.sellerFreightProviderId,
      },
    })
    .then((r) => r.data);
};
